import type { ContractKind } from "@/services/docuseal";

export type OriginationKind = "main_contract" | "refinance_contract";

export function originationPctStorageKey(borrowerId: number, kind: ContractKind): string {
  return `contracts:origination_fee_pct:${borrowerId}:${kind}`;
}

export function formatPercentPoints(val: number): string {
  const n = Number(val);
  if (!Number.isFinite(n)) return "";
  const rounded = Math.round(n * 100) / 100;
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2);
}

export function parseDecimalLike(raw: string): number | null {
  if (!raw) return null;
  const cleaned = String(raw).trim().replaceAll("$", "").replaceAll(",", "").replaceAll("%", "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function formatCurrency(n: number | null): string {
  if (n == null) return "";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function normalizeOriginationFeePct(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  return Math.round(Math.max(0, Math.min(25, raw)) * 100) / 100;
}

export function readStoredOriginationPct(borrowerId: number, kind: ContractKind): number | null {
  try {
    const raw = localStorage.getItem(originationPctStorageKey(borrowerId, kind));
    if (raw == null || raw === "") return null;
    const n = parseDecimalLike(raw);
    return n == null ? null : Math.max(0, Math.min(25, n));
  } catch {
    return null;
  }
}

export function writeStoredOriginationPct(
  borrowerId: number,
  kind: ContractKind,
  pct: number
): void {
  try {
    localStorage.setItem(originationPctStorageKey(borrowerId, kind), formatPercentPoints(pct));
  } catch {
    /* ignore quota / private mode */
  }
}

function recordedOriginationPct(raw: number | string | null | undefined): number | null {
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : Math.max(0, Math.min(25, n));
}

/**
 * OfferDocs resolution: localStorage → assessment/offer percent → infer from
 * (total − disbursed)/total on main only → org default when total > 0.
 */
export function resolveOriginationPct(input: {
  kind: OriginationKind;
  borrowerId: number;
  recordedPct: number | string | null | undefined;
  values: Record<string, string>;
  defaultCommissionPct: number;
}): number {
  const fromLs = readStoredOriginationPct(input.borrowerId, input.kind);
  const fromRecorded = recordedOriginationPct(input.recordedPct);
  const total = parseDecimalLike(input.values.total_amount_funds_provided ?? "");
  const disbursed = parseDecimalLike(input.values.amount_disbursed ?? "");

  let pct = 0;
  let inferred = false;
  if (fromLs != null) {
    pct = fromLs;
  } else if (fromRecorded != null) {
    pct = fromRecorded;
  } else if (
    input.kind !== "refinance_contract" &&
    total != null &&
    total > 0 &&
    disbursed != null &&
    disbursed <= total
  ) {
    pct = Math.max(0, Math.min(25, ((total - disbursed) / total) * 100));
    inferred = true;
  }

  if (fromLs == null && fromRecorded == null && !inferred && total != null && total > 0) {
    pct = input.defaultCommissionPct;
  }

  return normalizeOriginationFeePct(pct);
}

/** Writes origination, disbursed, net, and dollar-cost. Refinance subtracts balance from disbursed/net only. */
export function applyOriginationFields(
  values: Record<string, string>,
  pct: number,
  opts: { kind: OriginationKind; balance?: number | string | null }
): Record<string, string> {
  const total = parseDecimalLike(values.total_amount_funds_provided ?? "");
  if (total == null || !(total > 0)) return values;
  const balanceAmt = opts.kind === "refinance_contract" ? Number(opts.balance ?? 0) || 0 : 0;
  const disbursedBeforeBalance = total * (1 - pct / 100);
  const disbursedAmt = disbursedBeforeBalance - balanceAmt;
  const next: Record<string, string> = {
    ...values,
    amount_disbursed: formatCurrency(disbursedAmt),
    net_funds_provided: formatCurrency(disbursedAmt),
    origination_fee: formatCurrency((total * pct) / 100),
  };
  const payback = parseDecimalLike(values.total_amount_to_be_paid ?? "");
  if (payback != null) {
    next.total_dollar_cost = formatCurrency(payback - disbursedBeforeBalance);
  }
  return next;
}
